import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useDailyRewards } from '../hooks/useDailyRewards';
import { setDailyRewardModal } from '../../../store/slices/uiSlice';
import '../styles/dailyRewardPage.css';
import Balance from '../../wallet/components/Balance';
import Preloader from '../../../shared/components/Preloader';
import { useTranslation } from 'react-i18next';

const DailyRewardPage = ({ onComplete, balanceRef }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    isLoading,
    isClaiming,
    error,
    currentStreak,
    claimReward,
    getCurrentDayConfig,
    getDayConfig,
    clearError,
    loadStreakInfo
  } = useDailyRewards();

  const claimInFlightRef = useRef(false);
  const [animate, setAnimate] = useState(false);
  const [flyingItems, setFlyingItems] = useState([]);
  const reelRef = useRef();
  const [day, setDay] = useState(0);

  // Загружаем информацию о стрике при монтировании
  useEffect(() => {
    console.log('🚀 [DailyRewardPage] Component mounted, loading streak info');
    loadStreakInfo();
  }, [loadStreakInfo]);

  // Обновляем день когда приходят данные о стрике
  // currentStreak IS the current day number (e.g. 3 = day 3), not the previous
  useEffect(() => {
    setDay(currentStreak);
  }, [currentStreak]);

  const animateReward = (icon, destinationRef) => {
    const originalImg = document.querySelector(`.reward-item img[src="${icon}"]`);
    const targetRect = destinationRef.current?.getBoundingClientRect();
    const originRect = originalImg?.getBoundingClientRect();
  
    if (!originalImg || !targetRect || !originRect) return;
  
    const flying = originalImg.cloneNode(true);
    flying.classList.add('flying-original');
    flying.style.left = `${originRect.left}px`;
    flying.style.top = `${originRect.top}px`;
  
    document.body.appendChild(flying);
    originalImg.style.visibility = 'hidden';
  
    void flying.offsetWidth;
  
    flying.style.transform = `translate(${targetRect.left - originRect.left}px, ${targetRect.top - originRect.top}px) scale(0.5)`;
    flying.style.opacity = '0';
  
    setTimeout(() => {
      flying.remove();
      originalImg.style.visibility = 'visible';
    }, 1000);
  };

  const handleClaimReward = async () => {
    // Synchronous guard — prevents duplicate requests even before Redux re-renders
    if (claimInFlightRef.current || isClaiming) return;
    claimInFlightRef.current = true;

    try {
      clearError();
      const result = await claimReward();

      // Animate rewards flying to balance
      const coinRef = balanceRef?.current?.coin;
      const ticketRef = balanceRef?.current?.ticket;
      animateReward('/coin_icon.png', { current: coinRef });
      animateReward('/ticket_icon.png', { current: ticketRef });

      setTimeout(() => {
        dispatch(setDailyRewardModal(false));
        if (onComplete) {
          onComplete();
        } else {
          navigate('/');
        }
      }, 1000);
      // claimInFlightRef stays true until component unmounts
    } catch (error) {
      if (error.message && error.message.includes('already claimed')) {
        setTimeout(() => {
          dispatch(setDailyRewardModal(false));
          if (onComplete) onComplete();
          else navigate('/');
        }, 2000);
        // claimInFlightRef stays true until component unmounts
      } else {
        // Real error — allow retry
        claimInFlightRef.current = false;
      }
    }
  };

  const currentDayConfig = getCurrentDayConfig();

  console.log('🎨 [DailyRewardPage] Rendering with state:', {
    isLoading,
    isClaiming,
    error,
    currentStreak,
    day,
    currentDayConfig
  });

  useEffect(() => {
    
    const timeout = setTimeout(() => {
      setAnimate(true);
      // setAnimate(false);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [day]);

  return (
    <>
      {/* Flying animation items */}
      {flyingItems.map((item) => (
        <img
          key={item.id}
          src={item.icon}
          className="flying-item"
          style={{
            left: item.startX,
            top: item.startY,
            '--x': `${item.endX - item.startX}px`,
            '--y': `${item.endY - item.startY}px`,
          }}
        />
      ))}

      <div className="daily-container">
        <div className="balance-wrapper">
          <Balance ref={balanceRef} />
        </div>

        <div className="rewards-gradient"></div>

        <div className="rewards-card">
          {isLoading ? (
            <Preloader />
          ) : (
          <>
          <div className="rewards-card-inner">
            <div className="day-reel-container">
              <div className={`day-reel ${animate ? 'spin' : ''}`} ref={reelRef}>
                <div className="reel-number reel-number--prev">{day > 0 ? day - 1 : 0}</div>
                <div className="reel-number">{day}</div>
              </div>
            </div>
          </div>

          <h2>{t('daily.title', 'Your daily rewards')}</h2>

          {/* Streak progress dots (currentStreak is 1-based day number) */}
          <div className="streak-dots">
            {[...Array(7)].map((_, i) => (
              <span
                key={i}
                className={`streak-dot ${i < currentStreak - 1 ? 'streak-dot--done' : ''} ${i === currentStreak - 1 ? 'streak-dot--current' : ''}`}
              />
            ))}
          </div>

          {/* Current rewards */}
          <div className="reward-list">
            <div className="reward-item">
              <img src="/coin_icon.png" alt={t('daily.coins', 'Datum Coins')} />
              <div className="daily-reward-amount">{currentDayConfig.coins}</div>
              <div className="daily-reward-label">{t('daily.coins', 'Datum Coins')}</div>
            </div>
            <div className="reward-item">
              <img src="/ticket_icon.png" alt={t('daily.tickets', 'Play Tickets')} />
              <div className="daily-reward-amount">{currentDayConfig.tickets}</div>
              <div className="daily-reward-label">{t('daily.tickets', 'Play Tickets')}</div>
            </div>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <div style={{"paddingLeft":"16px","zIndex":"1","paddingRight":"16px"}}>
            <p className="info-text">
              {t('daily.come_back', 'Come back tomorrow for check-in day')}<br />
              <span className="tip">{t('daily.tip', 'Tip: Skipping a day resets your check-in.')}</span>
            </p>

            <button
                className={`app-btn app-btn--green ${isClaiming ? ' is-disabled' : ''}`}
                onClick={handleClaimReward}
                disabled={isClaiming}
            >
              {isClaiming ? t('lair.claiming', 'Claiming…') : t('daily.claim', 'Get Reward & Continue')}
            </button>
          </div>
          </>
          )}
        </div>
      </div>
    </>
  );
};

export default DailyRewardPage;

